import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import SectorRequestDialog from "@/components/marketing/SectorRequestDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  ArrowRight, ArrowUpRight, Phone, PhoneIncoming, PhoneForwarded,
  ShieldCheck, Globe2, CheckCircle2, Loader2,
  Send, Sparkles, MicVocal, Timer, Wallet, Building2,
  // Orbital icons — sectors
  Car, Home, HeartPulse, Stethoscope, Landmark, CreditCard,
  Briefcase, Receipt, Sun, House, Wrench, Scale, Package,
  Shield, RefreshCcw, KeyRound, DoorOpen, Bath, Cross, Lock,
  Users,
  // Compliance layer + countries
  Fingerprint, ScrollText, Radio,
  // Pillars redesign
  Activity, Waves,
} from "lucide-react";

/**
 * /pay-per-call — Two-sided pay-per-call marketplace.
 *
 * Design language mirrors /solutions:
 *  · Hero text-left + 3-column horizontal call-routing flow visual (right)
 *  · Three "pillar" cards below (Buyer / Publisher / Compliance)
 *  · Bento grid of features with mixed card sizes
 *  · Rate grid, countries strip, form
 *
 * Backend: POST /api/pay-per-call/enquiry — mode: "buyer" | "publisher".
 */

// ─── Pay-per-call sector rate card — 2026 UK / US market ranges ───
// Ordered by verticals: Insurance · Finance · Home Services · Legal · Health.
const SECTORS = [
  // Insurance
  { key: "auto-insurance",       name: "Auto Insurance",         cpc: "£14–£45",  desc: "Live-transfer quotes, geo-verified",  group: "Insurance" },
  { key: "home-insurance",       name: "Home Insurance",         cpc: "£16–£50",  desc: "Property-verified, quote-ready",       group: "Insurance" },
  { key: "life-insurance",       name: "Life Insurance",         cpc: "£20–£60",  desc: "Age + health pre-qualified",           group: "Insurance" },
  { key: "final-expense",        name: "Final Expense",          cpc: "£28–£85",  desc: "US · 50+ age band, pre-consented",     group: "Insurance" },
  { key: "medicare-health",      name: "Medicare / Health",      cpc: "£24–£70",  desc: "US only · TCPA-compliant",             group: "Insurance" },
  { key: "auto-warranty",        name: "Auto Warranty",          cpc: "£18–£65",  desc: "Vehicle-verified, mileage-band checked", group: "Insurance" },
  { key: "home-warranty",        name: "Home Warranty",          cpc: "£16–£55",  desc: "Homeowner-verified, ready-to-buy",     group: "Insurance" },

  // Finance
  { key: "mortgages",            name: "Mortgages",              cpc: "£40–£120", desc: "DIP-aware, refi + purchase",           group: "Finance" },
  { key: "reverse-mortgage",     name: "Reverse Mortgage",       cpc: "£45–£150", desc: "US only · 62+ homeowner qualified",    group: "Finance" },
  { key: "auto-loans",           name: "Auto Loans / Refi",      cpc: "£22–£85",  desc: "Credit-band + LTV-aware",              group: "Finance" },
  { key: "debt-credit",          name: "Debt / Credit",          cpc: "£20–£72",  desc: "Affordability-checked",                group: "Finance" },
  { key: "tax-debt",             name: "Tax Debt Relief",        cpc: "£40–£160", desc: "£5k+ balance qualified",               group: "Finance" },
  { key: "business-finance",     name: "Business Finance",       cpc: "£48–£145", desc: "Trading 12mo+ pre-qualified",          group: "Finance" },

  // Home services
  { key: "solar",                name: "Solar",                  cpc: "£32–£120", desc: "Homeowner-verified",                   group: "Home Services" },
  { key: "roofing",              name: "Roofing",                cpc: "£40–£145", desc: "Damage type + roof age",               group: "Home Services" },
  { key: "hvac-plumbing",        name: "HVAC / Plumbing",        cpc: "£28–£95",  desc: "Emergency + install intent",           group: "Home Services" },
  { key: "windows-doors",        name: "Windows & Doors",        cpc: "£30–£110", desc: "Property-verified, quote-ready",       group: "Home Services" },
  { key: "bathroom-kitchen",     name: "Bath / Kitchen Remodel", cpc: "£38–£130", desc: "Budget-band + timeline verified",      group: "Home Services" },
  { key: "home-security",        name: "Home Security",          cpc: "£24–£85",  desc: "Homeowner-verified, install intent",   group: "Home Services" },

  // Legal & health
  { key: "legal-pi",             name: "Legal · Personal Injury", cpc: "£40–£240", desc: "Fact-pattern pre-vetted",             group: "Legal & Health" },
  { key: "legal-family",         name: "Legal · Family Law",     cpc: "£30–£150", desc: "Case-type + jurisdiction verified",    group: "Legal & Health" },
  { key: "rehab-treatment",      name: "Rehab & Treatment",      cpc: "£45–£220", desc: "US · insurance-verified admissions",   group: "Legal & Health" },
  { key: "credit-repair",        name: "Credit Repair",          cpc: "£25–£85",  desc: "Credit-band + FICO tier verified",     group: "Finance" },
  { key: "water-damage",         name: "Water Damage / Restoration", cpc: "£40–£140", desc: "Emergency + insurance-covered leads", group: "Home Services" },
];

const COUNTRIES = [
  { flag: "🇬🇧", name: "United Kingdom", hint: "GDPR / PECR / FCA-aware" },
  { flag: "🇺🇸", name: "United States",  hint: "TCPA / STIR-SHAKEN" },
  { flag: "🇨🇦", name: "Canada",         hint: "CASL / CRTC" },
  { flag: "🇦🇺", name: "Australia",      hint: "Do Not Call · Spam Act" },
  { flag: "🇮🇪", name: "Ireland",        hint: "GDPR / ComReg" },
];

// ─── Three pillars (mirror /solutions structure) ───
const PILLARS = [
  {
    n: "01", Icon: PhoneIncoming, name: "For Buyers · Advertisers", tag: "The demand side",
    intro:
      "Post a campaign, set your buyer-duration threshold, and get billed only for live inbound calls that hit it. Every call carries recorded consent, geo, timestamp and IP — audit-ready by default.",
    bullets: [
      "Inbound-only, live callers — never cold outbound",
      "Buyer-duration billing (60 / 90 / 120s)",
      "Real-time routing on sector + geo + hours",
      "TCPA + PECR consent-first flow",
    ],
    cta: "Post a buyer campaign", mode: "buyer",
  },
  {
    n: "02", Icon: PhoneForwarded, name: "For Publishers · Call Centres", tag: "The supply side",
    intro:
      "Point your Google or Meta traffic — or your existing outbound seats — at a Connexis tracking number. We route each call to a matched buyer in real-time and pay you weekly on billable duration.",
    bullets: [
      "Weekly Friday payouts · no minimums",
      "24+ sectors across five countries",
      "Unlimited tracking numbers · per-campaign attribution",
      "Real-time buyer match on every inbound call",
    ],
    cta: "Apply as a publisher", mode: "publisher",
  },
  {
    n: "03", Icon: ShieldCheck, name: "Compliance & Recording", tag: "The regulatory layer",
    intro:
      "Same infrastructure that routes 240,000 enquiries a month, extended to voice. Every call is recorded, retained, consent-verified and jurisdiction-tagged so regulators, buyers and internal QA can all trust the same evidence.",
    bullets: [
      "STIR/SHAKEN attestation on US traffic",
      "DNC + suppression scrubbing before routing",
      "Full recording + 24 month retention",
      "Recorded consent artefact per call",
    ],
    cta: "Read the compliance stack", mode: null, href: "/trust",
  },
];

/* -------------------------------------------------------- */

export default function PayPerCall() {
  const formRef = useRef(null);
  const [sectorDialog, setSectorDialog] = useState(false);
  const scrollToForm = (mode) => {
    formRef.current?.setMode?.(mode);
    formRef.current?.el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <MarketingLayout>
      <div className="bg-white" data-testid="pay-per-call-page">

        {/* ─── HERO ─────────────────────────────────────────────── */}
        <section className="relative border-b border-slate-200 overflow-hidden bg-gradient-to-b from-white to-slate-50/30">
          <div className="absolute inset-0 dot-grid dot-grid-fade opacity-70" />
          <div className="absolute -top-32 -right-20 w-[700px] h-[700px] rounded-full bg-[#3d7bff]/[0.07] blur-3xl pointer-events-none" />

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
            <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
              {/* LEFT — copy + CTAs */}
              <div>
                <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-semibold text-[#0055FF]">
                  <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Pay-per-call · live marketplace
                </div>
                <h1 className="mt-3 text-5xl sm:text-6xl lg:text-[64px] tracking-[-0.045em] font-semibold text-slate-900 leading-[1.02]" data-testid="ppc-title">
                  The pay-per-call layer for{" "}
                  <span className="bg-gradient-to-r from-slate-900 via-[#0055FF] to-[#3d7bff] bg-clip-text text-transparent">
                    the UK &amp; US.
                  </span>
                </h1>
                <p className="mt-5 max-w-xl text-[17px] text-slate-600 leading-relaxed">
                  A two-sided marketplace for live inbound calls. Buyers post sector rules, call centres and
                  affiliates place volume, Connexis routes, filters and bills — with buyer-duration guarantees and
                  full TCPA / PECR compliance baked in.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Button
                    data-testid="ppc-hero-buy"
                    onClick={() => scrollToForm("buyer")}
                    className="rounded-full px-5 h-11 bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]"
                  >
                    I want to buy calls <ArrowUpRight className="size-4" />
                  </Button>
                  <Button
                    data-testid="ppc-hero-sell"
                    variant="outline"
                    onClick={() => scrollToForm("publisher")}
                    className="rounded-full px-5 h-11 border-slate-300 hover:border-[#0055FF]/40"
                  >
                    I run a call centre
                  </Button>
                  <a href="#sectors" className="text-[13px] font-medium text-[#0055FF] hover:underline inline-flex items-center gap-1 ml-1">
                    See sector rates <ArrowRight className="size-3.5" />
                  </a>
                </div>

                {/* Trust strip */}
                <div className="mt-10 pt-8 border-t border-slate-200 grid grid-cols-3 gap-6 max-w-md">
                  <Stat v="240k+" l="calls routed / mo" />
                  <Stat v="60–120s" l="buyer-duration" />
                  <Stat v="5" l="countries live" />
                </div>
              </div>

              {/* RIGHT — orbital call ecosystem (sectors · countries · compliance) */}
              <div className="relative">
                <CallRoutingFlow />
                {/* Legend — Sectors · Operations · Compliance layer */}
                <div className="mt-4 grid grid-cols-3 gap-2 text-[11.5px] text-center text-slate-600">
                  <LegendChip color="bg-white border border-slate-300" label="Sectors" />
                  <LegendChip color="bg-slate-900" label="Operations" />
                  <LegendChip color="bg-[#0055FF]" label="Compliance layer" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── THREE PILLARS — Kinetic editorial showcase ────── */}
        <PillarsShowcase onCta={(mode, href) => href ? null : scrollToForm(mode)} />

        {/* ─── BENTO — how it works + capabilities ─────────────── */}
        <section className="py-24 bg-slate-50/60 border-y border-slate-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-12">
              <div>
                <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Every call, engineered</p>
                <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.035em] font-semibold text-slate-900 leading-[1.02]">
                  From dial-in to billable in seconds.
                </h2>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-medium text-slate-600">
                <Sparkles className="size-3 text-[#0055FF]" /> 4 steps · 1 routed layer
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 lg:auto-rows-[180px] gap-4">
              {/* HERO BENTO — dark, spans 2×2 */}
              <div className="col-span-2 row-span-2 rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-[#0c1b3a] text-white p-5 sm:p-7 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.10] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#3d7bff]/30 blur-3xl pointer-events-none" />

                <div className="relative h-full flex flex-col">
                  <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-[#7da9ff] font-semibold">
                    <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live call in progress
                  </div>
                  <h3 className="mt-3 text-[22px] sm:text-[26px] tracking-[-0.025em] font-semibold leading-[1.1]">
                    Every dial matched to the right buyer in <span className="bg-gradient-to-r from-white to-[#7da9ff] bg-clip-text text-transparent">under 300ms.</span>
                  </h3>

                  <CallTimelineViz />

                  <div className="mt-auto pt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11.5px] text-white/55">
                    <span>Sub-300ms routing</span>
                    <span className="text-white/30">·</span>
                    <span><span className="text-emerald-400 font-mono">Billable</span> at 90s</span>
                    <span className="text-white/30">·</span>
                    <span><span className="text-white font-mono">24</span> sectors live</span>
                  </div>
                </div>
              </div>

              <BentoCard Icon={Phone}       t="Caller dials" d="A consumer clicks a Connexis-supplied number in your ad, article, comparison page or app." />
              <BentoCard Icon={MicVocal}    t="IVR pre-qualifies" d="Sector-tuned IVR captures intent, geo and consent in under 20 seconds — before any buyer touches the call." />
              <BentoCard Icon={Radio}       t="Match to buyer" d="The router matches call metadata against live buyer rules — sector, geo, hours, cap — and connects the best fit." />
              <BentoCard Icon={Timer}       t="Bill on duration" d="Call routes live to the buyer. Billable at 60/90/120s per buyer's own rules." />
              <BentoCard Icon={Fingerprint} t="Consent artefact" d="TCPA + PECR consent, timestamp, IP and user-agent captured with every call." />
              <BentoCard Icon={ScrollText}  t="Full recording" d="24-month retention, sector-tagged, DSAR-ready. Buyer + publisher + regulator all trust one source." />
              <BentoCard Icon={Wallet}      t="Friday payouts" d="Publishers paid weekly on the prior week's billable calls. Stripe Connect or direct bank." />
              <BentoCard Icon={Building2}   t="Buyers billed Mon" d="Consolidated Monday invoicing across every campaign. Wallet debit or invoice on terms." />
            </div>
          </div>
        </section>

        {/* ─── SECTOR RATE GRID ────────────────────────────────── */}
        <section id="sectors" className="py-24 bg-white scroll-mt-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">Sector rate card</p>
              <h2 className="mt-3 text-4xl sm:text-5xl tracking-[-0.035em] font-semibold text-slate-900 leading-[1.02]">24 sectors. Live rates.</h2>
              <p className="mt-4 text-slate-600 text-[15.5px] leading-relaxed max-w-2xl">
                Ranges reflect current UK / US buyer rates for a qualified, billable call. Actual payouts vary with
                geo, hours, duration threshold and source quality — rates are set per campaign, live at routing time.
              </p>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5" data-testid="ppc-sector-grid">
              {SECTORS.map((s) => (
                <div
                  key={s.key}
                  className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#0055FF]/40 hover:shadow-[0_16px_50px_-24px_rgba(0,85,255,0.25)] hover:-translate-y-0.5 transition-all"
                  data-testid={`ppc-sector-${s.key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[9.5px] font-mono uppercase tracking-[0.14em] text-slate-500">
                          {s.group}
                        </span>
                      </div>
                      <div className="mt-1.5 text-[14.5px] font-semibold text-slate-900 tracking-tight">{s.name}</div>
                      <div className="mt-1 text-[12px] text-slate-500 leading-snug">{s.desc}</div>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-slate-50 border border-slate-200 group-hover:bg-[#0055FF]/[0.06] group-hover:border-[#0055FF]/25 transition-colors px-2.5 py-1 text-[11.5px] font-mono font-semibold text-slate-900 tabular-nums tracking-wide shrink-0">
                      {s.cpc}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Countries strip */}
            <div className="mt-14">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Countries live</div>
              <div className="flex flex-wrap gap-2.5" data-testid="ppc-countries">
                {COUNTRIES.map((c) => (
                  <div
                    key={c.name}
                    className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-[13px] text-slate-700 shadow-soft"
                    data-testid={`ppc-country-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="text-lg leading-none" aria-hidden>{c.flag}</span>
                    <span className="font-medium text-slate-900">{c.name}</span>
                    <span className="text-[11.5px] text-slate-500 font-mono">· {c.hint}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Unlisted sector CTA — we're always expanding the marketplace */}
            <div
              className="mt-10 rounded-2xl border border-dashed border-[#0055FF]/30 bg-[#0055FF]/[0.03] p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
              data-testid="ppc-unlisted-cta"
            >
              <div className="inline-grid place-items-center size-11 rounded-xl bg-[#0055FF]/10 text-[#0055FF] ring-1 ring-[#0055FF]/20 shrink-0">
                <Sparkles className="size-5" strokeWidth={1.7} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-slate-900 tracking-tight">Not seeing your sector?</div>
                <p className="mt-1 text-[13.5px] text-slate-600 leading-relaxed">
                  If you produce inbound calls in a vertical we haven&apos;t listed — or you&apos;d buy calls in a sector we
                  haven&apos;t launched yet — get in touch. We onboard new sectors every month.
                </p>
              </div>
              <button
                onClick={() => setSectorDialog(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white px-4 h-10 text-[13px] font-semibold shadow-[0_8px_24px_rgba(0,85,255,0.35)] whitespace-nowrap shrink-0"
                data-testid="ppc-unlisted-cta-button"
              >
                Request this sector <ArrowRight className="size-3.5" />
              </button>
            </div>
          </div>
        </section>

        <SectorRequestDialog open={sectorDialog} onOpenChange={setSectorDialog} defaultRole="buyer" />

        {/* ─── FORM ─────────────────────────────────────────────── */}
        <PayPerCallForm ref={formRef} />

        {/* ─── FOOTER CTA ──────────────────────────────────────── */}
        <section className="py-24 bg-white">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h3 className="text-3xl sm:text-4xl tracking-[-0.02em] font-semibold text-slate-900">Ready to route your first call?</h3>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
              Buyers get live in 3-5 working days. Call centres can be placing traffic on Connexis numbers within a week.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={() => document.querySelector('[data-testid="ppc-form"]')?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-full px-5 h-11 bg-[#0055FF] hover:bg-[#0044CC] shadow-[0_8px_24px_rgba(0,85,255,0.35)]"
                data-testid="ppc-footer-cta"
              >
                Talk to us <ArrowRight className="size-4" />
              </Button>
              <Link to="/docs">
                <Button variant="outline" className="rounded-full px-5 h-11 border-slate-300">
                  Read the API docs
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}

/* ═════════════════════════════════════════════════════════════════
   PILLARS SHOWCASE — Kinetic editorial layout
   Replaces the templatey 3-stacked-cards look with a dark theatrical
   stage: an animated routing ticker across the top, then three
   staggered "spotlight" cards each with its own signature mini-visual,
   glow accent, and a huge decorative ordinal digit as a watermark.
   ═════════════════════════════════════════════════════════════════ */
function PillarsShowcase({ onCta }) {
  const sectionRef = useRef(null);

  // Subtle mouse parallax — updates two CSS vars (--mx, --my) on the
  // section. Each spotlight visual reads these vars via calc() and
  // translates itself at a different depth for a real "signature moment"
  // feel. Fully disabled for prefers-reduced-motion users.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let raf = null;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;   // -1 → 1
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--mx", x.toFixed(3));
        el.style.setProperty("--my", y.toFixed(3));
      });
    };
    const onLeave = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--mx", "0");
        el.style.setProperty("--my", "0");
      });
    };
    el.addEventListener("mousemove", onMove, { passive: true });
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#070c1a] py-24 lg:py-28 text-white"
      style={{ "--mx": 0, "--my": 0 }}
      data-testid="ppc-pillars-showcase"
    >
      {/* Backdrop layers */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
      <div className="absolute -top-40 left-1/3 w-[620px] h-[620px] rounded-full bg-[#0055FF]/[0.18] blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-[520px] h-[520px] rounded-full bg-emerald-500/[0.08] blur-[120px] pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(7,12,26,0)_0%,rgba(7,12,26,0.9)_75%,#070c1a_100%)] pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header + live ticker */}
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-8 lg:gap-14 mb-16 items-end">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#7da9ff]">Two sides · one routed layer</p>
            <h2 className="mt-3 text-4xl sm:text-5xl lg:text-[52px] tracking-[-0.035em] font-semibold leading-[1.02]">
              Buy calls. Sell calls.{" "}
              <span className="bg-gradient-to-r from-white via-[#7da9ff] to-emerald-300 bg-clip-text text-transparent">
                Trust every second.
              </span>
            </h2>
          </div>
          <RoutingTicker />
        </div>

        {/* Three spotlights */}
        <div className="grid lg:grid-cols-3 gap-5 lg:gap-6">
          {PILLARS.map((p, i) => (
            <Spotlight
              key={p.n}
              index={i}
              {...p}
              onCta={() => (p.href ? null : onCta?.(p.mode))}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * RoutingTicker — a compact live-routing feed in the pillars header.
 * Six cycling events; each surfaces sector + geo + duration + status.
 */
function RoutingTicker() {
  const EVENTS = [
    { t: "0.24s", s: "Auto Insurance",   g: "London",     d: "94s",  ok: true  },
    { t: "0.31s", s: "Solar",            g: "Manchester", d: "72s",  ok: true  },
    { t: "0.19s", s: "Mortgages",        g: "New York",   d: "112s", ok: true  },
    { t: "0.27s", s: "Legal · PI",       g: "Dallas",     d: "68s",  ok: true  },
    { t: "0.22s", s: "Home Warranty",    g: "Bristol",    d: "88s",  ok: true  },
    { t: "0.29s", s: "Medicare",         g: "Phoenix",    d: "121s", ok: true  },
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % EVENTS.length), 1900);
    return () => clearInterval(id);
  }, []);
  const visible = [EVENTS[idx % EVENTS.length], EVENTS[(idx + 1) % EVENTS.length], EVENTS[(idx + 2) % EVENTS.length]];

  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-3 shadow-[0_10px_60px_-20px_rgba(0,85,255,0.4)]"
      data-testid="ppc-pillars-ticker"
    >
      <div className="flex items-center justify-between px-2 pb-2">
        <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.2em] uppercase text-white/60 font-semibold">
          <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Live routing · last 6s
        </div>
        <span className="text-[10.5px] font-mono text-white/40">LDN · NYC · DXB</span>
      </div>
      <div className="space-y-1.5">
        {visible.map((e, i) => (
          <div
            key={`${idx}-${i}`}
            className="flex items-center gap-3 rounded-lg bg-white/[0.03] border border-white/[0.06] px-3 py-2 animate-[tickerIn_0.5s_ease-out]"
          >
            <span className="text-[10.5px] font-mono text-[#7da9ff] w-10 tabular-nums">{e.t}</span>
            <span className="text-[12.5px] text-white/90 truncate flex-1">{e.s} · {e.g}</span>
            <span className="text-[10.5px] font-mono text-emerald-300 tabular-nums">{e.d}</span>
            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300">
              <span className="size-1.5 rounded-full bg-emerald-400" /> billable
            </span>
          </div>
        ))}
      </div>
      <style>{`@keyframes tickerIn { from { opacity: 0; transform: translateY(3px) } to { opacity: 1; transform: none } }`}</style>
    </div>
  );
}

/**
 * Spotlight — one pillar card. Elite treatment:
 *  · fixed-height signature visual on top (a mini "product snapshot")
 *  · consistent content rhythm: eyebrow → icon+title → body → bullets → CTA
 *  · flex-col with CTA anchored to bottom via mt-auto so all 3 cards align
 *  · single subtle top-right accent glow (no huge watermark digit, no offset)
 */
function Spotlight({ index, n, Icon, name, tag, intro, bullets, cta, mode, href, onCta }) {
  const accent = ["#3d7bff", "#7da9ff", "#34d399"][index];
  // Per-index parallax depths for variety — outer glow drifts more,
  // visual inner drifts subtly. Values fold into CSS calc() via --mx/--my.
  const glowDepth   = 14 + index * 2;   // 14 / 16 / 18 px
  const visualDepth = 5  + index * 1.5; // 5  / 6.5 / 8  px
  return (
    <div
      className="group relative flex flex-col rounded-2xl border border-white/[0.08] bg-gradient-to-b from-white/[0.03] to-white/[0.015] overflow-hidden hover:border-white/[0.18] transition-colors"
      data-testid={`ppc-pillar-${n}`}
    >
      {/* Subtle single-corner accent — parallax with cursor */}
      <div
        className="absolute -top-24 -right-16 w-56 h-56 rounded-full blur-3xl pointer-events-none opacity-70 will-change-transform"
        style={{
          background: `${accent}22`,
          transform: `translate3d(calc(var(--mx,0) * ${glowDepth}px), calc(var(--my,0) * ${glowDepth * 0.7}px), 0)`,
          transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
        }}
      />

      {/* Signature visual — fixed height, inner drifts subtly with cursor */}
      <div className="relative h-44 border-b border-white/[0.06] overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(circle at 20% 100%, ${accent}18, transparent 55%)` }}
        />
        <div
          className="absolute inset-0 will-change-transform"
          style={{
            transform: `translate3d(calc(var(--mx,0) * ${visualDepth}px), calc(var(--my,0) * ${visualDepth * 0.6}px), 0)`,
            transition: "transform 0.45s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <SpotlightVisual index={index} accent={accent} />
        </div>
      </div>

      {/* Content column — flex-col so CTA sticks to bottom */}
      <div className="relative flex-1 flex flex-col p-6 sm:p-7">
        {/* Eyebrow row: tag on the left, ordinal on the right */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-medium">{tag}</span>
          <span className="text-[10px] font-mono tabular-nums text-white/30">{n} / 03</span>
        </div>

        {/* Icon + title */}
        <div className="mt-4 flex items-center gap-3">
          <span
            className="inline-grid place-items-center size-9 rounded-lg border"
            style={{
              background: `${accent}14`,
              borderColor: `${accent}33`,
              color: accent,
            }}
          >
            <Icon className="size-[18px]" strokeWidth={1.7} />
          </span>
          <h3 className="text-[20px] tracking-[-0.02em] font-semibold text-white leading-tight">{name}</h3>
        </div>

        {/* Description */}
        <p className="mt-3 text-[13.5px] text-white/55 leading-relaxed">{intro}</p>

        {/* Bullets */}
        <ul className="mt-5 space-y-2.5">
          {bullets.map((b, j) => (
            <li key={j} className="flex items-start gap-2.5 text-[13px] text-white/75 leading-relaxed">
              <span className="mt-[7px] shrink-0 size-1 rounded-full" style={{ background: accent }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>

        {/* CTA — anchored to bottom */}
        <div className="mt-auto pt-6">
          <div className="pt-5 border-t border-white/[0.06]">
            {href ? (
              <Link
                to={href}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white group/cta"
                data-testid={`ppc-pillar-cta-${n}`}
              >
                {cta}
                <ArrowRight
                  className="size-3.5 transition-transform group-hover/cta:translate-x-0.5"
                  style={{ color: accent }}
                />
              </Link>
            ) : (
              <button
                onClick={onCta}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white group/cta"
                data-testid={`ppc-pillar-cta-${n}`}
              >
                {cta}
                <ArrowRight
                  className="size-3.5 transition-transform group-hover/cta:translate-x-0.5"
                  style={{ color: accent }}
                />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SpotlightVisual — three cohesive "product snapshots" that all follow
 * the same visual grammar: mono eyebrow chip + accent glow + 3 tabular
 * data rows + a footer detail. Same rhythm, same restraint.
 */
function SpotlightVisual({ index, accent }) {
  if (index === 0) return <VisualBuyer accent={accent} />;
  if (index === 1) return <VisualPublisher accent={accent} />;
  return <VisualCompliance accent={accent} />;
}

/** Buyer: a mini live campaign card */
function VisualBuyer({ accent }) {
  return (
    <div className="absolute inset-0 p-5 flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.08] text-[9.5px] font-mono uppercase tracking-[0.14em] text-white/70">
            <span className="size-1 rounded-full bg-emerald-400 animate-pulse" />
            MC-4821 · Live
          </span>
          <span className="text-[9.5px] font-mono text-white/40">Auto Insurance</span>
        </div>
        <div className="mt-3 space-y-1">
          <SpecRow k="Buyer duration"  v="90s"  />
          <SpecRow k="Target CPC"      v="£45"  />
          <SpecRow k="Daily cap"       v="500"  />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between text-[9.5px] font-mono tabular-nums">
          <span className="text-white/50">228 / 500 calls</span>
          <span className="text-white/85">£8,664 today</span>
        </div>
        <div className="mt-1.5 h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{ width: "46%", background: `linear-gradient(to right, ${accent}, ${accent}AA)` }}
          />
        </div>
      </div>
    </div>
  );
}
function SpecRow({ k, v }) {
  return (
    <div className="flex items-center justify-between text-[11.5px]">
      <span className="text-white/50">{k}</span>
      <span className="font-mono tabular-nums text-white/90">{v}</span>
    </div>
  );
}

/** Publisher: a mini Friday-payout card */
function VisualPublisher({ accent }) {
  const rows = [
    { s: "Auto Insurance", n: 342, v: "£6,842" },
    { s: "Mortgages",      n: 128, v: "£5,974" },
    { s: "Solar",          n:  94, v: "£4,182" },
  ];
  return (
    <div className="absolute inset-0 p-5 flex flex-col justify-between">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[9.5px] font-mono uppercase tracking-[0.16em] text-white/40">Next payout</div>
          <div className="mt-0.5 text-[22px] font-semibold tracking-[-0.03em] text-white tabular-nums leading-none">
            £18,598
            <span className="ml-1.5 text-[11px] text-emerald-300 font-medium tracking-normal">net</span>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/25 text-[9.5px] font-mono uppercase tracking-[0.14em] text-emerald-300">
          <span className="size-1 rounded-full bg-emerald-400 animate-pulse" />
          Fri · in 4d
        </span>
      </div>
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.s} className="flex items-center justify-between text-[11.5px]">
            <span className="text-white/75 truncate">{r.s}</span>
            <span className="flex items-center gap-3 shrink-0">
              <span className="font-mono tabular-nums text-white/40">{r.n}</span>
              <span className="font-mono tabular-nums text-white/90 w-12 text-right">{r.v}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Compliance: a mini consent artefact with waveform */
function VisualCompliance({ accent }) {
  const bars = [6, 10, 14, 8, 16, 12, 20, 14, 22, 16, 10, 18, 12, 14, 8, 12, 6];
  return (
    <div className="absolute inset-0 p-5 flex flex-col justify-between">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/25 text-[9.5px] font-mono uppercase tracking-[0.14em] text-emerald-300">
          <ShieldCheck className="size-2.5" />
          Verified · TCPA
        </span>
        <span className="text-[9.5px] font-mono text-white/40 tabular-nums">CR-72918</span>
      </div>
      {/* Waveform */}
      <div className="flex items-center justify-center gap-[3px] h-10 my-1">
        <style>{`@keyframes wave { 0%,100% { transform: scaleY(1) } 50% { transform: scaleY(0.4) } }`}</style>
        {bars.map((h, i) => (
          <span
            key={i}
            className="w-[3px] rounded-full origin-center"
            style={{
              height: `${h}px`,
              background: `${accent}CC`,
              animation: `wave ${1.6 + (i % 5) * 0.2}s ease-in-out ${i * 0.05}s infinite`,
            }}
          />
        ))}
      </div>
      <div className="space-y-1">
        <SpecRow k="Duration" v="94s · London" />
        <SpecRow k="Retention" v="24 months" />
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   BENTO CARD
   ═════════════════════════════════════════════════════════════════ */
function BentoCard({ Icon, t, d }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#0055FF]/40 hover:shadow-md hover:-translate-y-0.5 transition-all">
      <span className="inline-grid place-items-center size-10 rounded-lg bg-[#0055FF]/[0.08] text-[#0055FF] ring-1 ring-[#0055FF]/15">
        <Icon className="size-5" strokeWidth={1.7} />
      </span>
      <div className="mt-4 font-semibold text-[14px] text-slate-900 tracking-tight">{t}</div>
      <div className="mt-1.5 text-[12.5px] text-slate-600 leading-relaxed">{d}</div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   PAY-PER-CALL ORBITAL — hero visual
   Mirrors /solutions OrbitalEcosystem exactly (three concentric orbits,
   counter-rotating icons that stay upright, pulsing central hub) but
   tailored to pay-per-call: sectors on the outer orbit, countries in
   the middle, compliance layer on the inner orbit.
   ═════════════════════════════════════════════════════════════════ */

const SECTOR_ORBIT = [
  { Icon: Car,        label: "Auto Insurance" },
  { Icon: Home,       label: "Home Services" },
  { Icon: HeartPulse, label: "Life Insurance" },
  { Icon: Stethoscope,label: "Medicare / Health" },
  { Icon: Landmark,   label: "Mortgages" },
  { Icon: CreditCard, label: "Debt / Credit" },
  { Icon: Briefcase,  label: "Business Finance" },
  { Icon: Receipt,    label: "Tax Debt" },
  { Icon: Sun,        label: "Solar" },
  { Icon: House,      label: "Roofing" },
  { Icon: Wrench,     label: "HVAC / Plumbing" },
  { Icon: Scale,      label: "Legal" },
];

const COUNTRY_ORBIT = [
  { Icon: Wallet,     label: "Wallet billing" },
  { Icon: Timer,      label: "Buyer-duration" },
  { Icon: RefreshCcw, label: "Retry + replay" },
  { Icon: Package,    label: "Signed payload" },
  { Icon: Users,      label: "Publisher payouts" },
];

const COMPLIANCE_ORBIT = [
  { Icon: MicVocal,    label: "IVR routing" },
  { Icon: Fingerprint, label: "Consent capture" },
  { Icon: Shield,      label: "DNC scrub" },
  { Icon: ScrollText,  label: "Call recording" },
];

function CallRoutingFlow() {
  return (
    <div className="relative w-full aspect-square max-w-[560px] mx-auto select-none" data-testid="ppc-live-visual">
      <style>{`
        @keyframes ppcOrbCW  { from { transform: translate(-50%, -50%) rotate(0deg);   } to { transform: translate(-50%, -50%) rotate(360deg);  } }
        @keyframes ppcOrbCCW { from { transform: translate(-50%, -50%) rotate(0deg);   } to { transform: translate(-50%, -50%) rotate(-360deg); } }
        @keyframes ppcCtrCW  { from { transform: rotate(0deg);  } to { transform: rotate(-360deg); } }
        @keyframes ppcCtrCCW { from { transform: rotate(0deg);  } to { transform: rotate(360deg);  } }
        @keyframes ppcHub {
          0%, 100% { box-shadow: 0 0 30px rgba(0,85,255,0.55), 0 0 80px rgba(0,85,255,0.28); }
          50%      { box-shadow: 0 0 55px rgba(0,85,255,0.85), 0 0 140px rgba(0,85,255,0.45); }
        }
        .ppc-orb-outer  { animation: ppcOrbCW   85s linear infinite; }
        .ppc-orb-middle { animation: ppcOrbCCW  55s linear infinite; }
        .ppc-orb-inner  { animation: ppcOrbCW   35s linear infinite; }
        .ppc-ctr-outer  { animation: ppcCtrCW   85s linear infinite; }
        .ppc-ctr-middle { animation: ppcCtrCCW  55s linear infinite; }
        .ppc-ctr-inner  { animation: ppcCtrCW   35s linear infinite; }
        .ppc-hub        { animation: ppcHub 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          [class*="ppc-orb-"], [class*="ppc-ctr-"], .ppc-hub { animation: none; }
        }
      `}</style>

      {/* Ambient radial wash — matches /solutions vibe */}
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(0,85,255,0.10),transparent_65%)] pointer-events-none" />

      {/* Three concentric orbits (identical technique to /solutions OrbitalEcosystem) */}
      <PpcOrbit sizePct={95} animClass="ppc-orb-outer"  counterClass="ppc-ctr-outer"  items={SECTOR_ORBIT}     tone="slate" size="lg" />
      <PpcOrbit sizePct={68} animClass="ppc-orb-middle" counterClass="ppc-ctr-middle" items={COUNTRY_ORBIT}    tone="dark"  size="xs" />
      <PpcOrbit sizePct={44} animClass="ppc-orb-inner"  counterClass="ppc-ctr-inner"  items={COMPLIANCE_ORBIT} tone="brand" size="xs" />

      {/* CENTER — Connexis call router hub */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none z-10">
        <span className="ppc-hub inline-grid place-items-center size-20 sm:size-24 rounded-2xl bg-[#0a1124] ring-1 ring-white/15">
          <Phone className="size-8 sm:size-9 text-white" strokeWidth={1.6} />
        </span>
      </div>
    </div>
  );
}

/**
 * Orbit primitive — cloned from OrbitalEcosystem so pay-per-call has an
 * identical premium aesthetic without cross-coupling the two pages.
 */
function PpcOrbit({ sizePct, animClass, counterClass, items, tone, size }) {
  const N = items.length;
  return (
    <div
      className={`absolute top-1/2 left-1/2 ${animClass}`}
      style={{ width: `${sizePct}%`, height: `${sizePct}%`, transform: "translate(-50%, -50%)" }}
    >
      <div className="absolute inset-0 rounded-full border border-dashed border-slate-300/55 pointer-events-none" aria-hidden />
      {items.map((it, i) => {
        const slot = (i * 360) / N;
        return (
          <div
            key={i}
            className="absolute inset-0 pointer-events-none"
            style={{ transform: `rotate(${slot}deg)` }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="pointer-events-none" style={{ transform: `rotate(${-slot}deg)` }}>
                <div className={`${counterClass} pointer-events-none`}>
                  <PpcOrbitIcon Icon={it.Icon} label={it.label} tone={tone} size={size} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PpcOrbitIcon({ Icon, label, tone = "slate", size = "lg" }) {
  const sizing = size === "xs"
    ? "size-9 rounded-lg [&_svg]:size-4"
    : "size-12 rounded-xl [&_svg]:size-5";
  const toneCls = {
    slate: "bg-white border border-slate-200 text-slate-700 shadow-soft",
    brand: "bg-[#0055FF] text-white shadow-[0_8px_24px_rgba(0,85,255,0.4)]",
    dark:  "bg-slate-900 text-white shadow-md",
  }[tone];
  return (
    <div className="relative inline-block group/orbit pointer-events-auto">
      <span className={`inline-grid place-items-center ${sizing} ${toneCls} cursor-pointer transition-transform group-hover/orbit:scale-110`}>
        <Icon strokeWidth={1.7} />
      </span>
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full whitespace-nowrap rounded-md bg-slate-900 text-white text-[10.5px] font-medium tracking-wide px-2 py-1 opacity-0 group-hover/orbit:opacity-100 transition-opacity z-[60] shadow-lg">
        {label}
      </span>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   CALL TIMELINE VIZ — bento hero card
   Ping → Route → Bid → Bill sequence with tiny animated timeline.
   ═════════════════════════════════════════════════════════════════ */
function CallTimelineViz() {
  const STEPS = [
    { t: "0.00s", label: "Caller dials",     color: "bg-[#7da9ff]" },
    { t: "0.18s", label: "IVR routes",       color: "bg-[#3d7bff]" },
    { t: "0.31s", label: "Matched buyer",    color: "bg-[#0055FF]" },
    { t: "01:30", label: "Billable hit",     color: "bg-emerald-500" },
  ];
  return (
    <div className="mt-5">
      <div className="relative h-2 rounded-full bg-white/[0.08] overflow-hidden">
        <span className="absolute left-0 top-0 h-full w-3/4 rounded-full bg-gradient-to-r from-[#3d7bff] via-[#0055FF] to-emerald-500 opacity-90" />
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2">
        {STEPS.map((s) => (
          <div key={s.label} className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className={`size-1.5 rounded-full ${s.color}`} />
              <span className="text-[9.5px] font-mono uppercase tracking-widest text-white/50">{s.t}</span>
            </div>
            <div className="mt-1 text-[11px] font-medium text-white/90 truncate">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   PAY-PER-CALL FORM — Glass cockpit on orbital dark canvas
   Left column: live enquiry mirror + rotating call ticker + trust strip
   Right column: glass-morphic form card with animated segmented mode
   toggle, chip constellation for sectors, and premium submit button.
   ═════════════════════════════════════════════════════════════════ */
const BUYER_INITIAL = { name: "", email: "", company: "", phone: "", country: "United Kingdom", buyer_sectors: [], buyer_monthly_calls: "", buyer_target_cpc_gbp: "", message: "" };
const PUB_INITIAL   = { name: "", email: "", company: "", phone: "", country: "United Kingdom", publisher_location: "", publisher_seats: "", publisher_sectors: [], publisher_current_cpc_gbp: "", message: "" };

const PayPerCallForm = React.forwardRef((_, extRef) => {
  const [mode, setMode] = useState("buyer");
  const [form, setForm] = useState(BUYER_INITIAL);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (extRef) {
      extRef.current = {
        setMode: (m) => {
          setMode(m);
          setForm(m === "buyer" ? BUYER_INITIAL : PUB_INITIAL);
        },
        el: rootRef.current,
      };
    }
  }, [extRef]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleSector = (key, listKey) => {
    setForm((f) => {
      const list = f[listKey] || [];
      return { ...f, [listKey]: list.includes(key) ? list.filter((x) => x !== key) : [...list, key] };
    });
  };
  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m);
    setForm(m === "buyer" ? BUYER_INITIAL : PUB_INITIAL);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and work email are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/pay-per-call/enquiry", { mode, ...form });
      setSent(true);
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — try again in a moment");
    } finally {
      setBusy(false);
    }
  };

  // Form completion % — used by the progress ring
  const completion = React.useMemo(() => {
    const required = ["name", "email"];
    const helpful  = mode === "buyer"
      ? ["company", "country", "buyer_sectors", "buyer_monthly_calls"]
      : ["company", "country", "publisher_sectors", "publisher_location", "publisher_seats"];
    const has = (k) => {
      const v = form[k];
      return Array.isArray(v) ? v.length > 0 : (v || "").toString().trim().length > 0;
    };
    const reqScore = required.filter(has).length / required.length;
    const helpScore = helpful.filter(has).length / helpful.length;
    const pct = Math.round((reqScore * 0.6 + helpScore * 0.4) * 100);
    return Math.min(100, pct);
  }, [form, mode]);

  return (
    <section
      ref={rootRef}
      id="enquire"
      className="relative overflow-hidden bg-[#070c1a] py-24 lg:py-28 scroll-mt-24"
      data-testid="ppc-form"
    >
      {/* Kinetic dark backdrop */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
           style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
      <div className="absolute -top-40 -left-20 w-[600px] h-[600px] rounded-full bg-[#0055FF]/[0.16] blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 w-[560px] h-[560px] rounded-full bg-emerald-500/[0.08] blur-[120px] pointer-events-none" />
      <FormOrbitBackdrop />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-[#7da9ff]">Talk to us</p>
          <h2 className="mt-3 text-4xl sm:text-5xl lg:text-[52px] tracking-[-0.035em] font-semibold text-white leading-[1.02]">
            Route your first call{" "}
            <span className="bg-gradient-to-r from-white via-[#7da9ff] to-emerald-300 bg-clip-text text-transparent">
              in a week.
            </span>
          </h2>
          <p className="mt-4 text-white/60 text-[15px] leading-relaxed max-w-xl">
            One form, two modes. Tell us if you&apos;re buying inbound calls or placing seats — we&apos;ll route your enquiry
            to the right desk and come back within one business day with next steps.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-[0.85fr_1.15fr] gap-6 lg:gap-8">
          {/* LEFT — live enquiry preview + rotating call ticker + trust */}
          <EnquiryPreview mode={mode} form={form} completion={completion} />

          {/* RIGHT — glass form card */}
          <div
            className="relative rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,85,255,0.4)]"
            data-testid="ppc-form-card"
          >
            {/* Corner accents */}
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#0055FF]/25 blur-3xl pointer-events-none" />

            <div className="relative p-6 sm:p-8">
              {/* Segmented mode toggle with sliding indicator */}
              <div
                className="relative inline-flex w-full rounded-full border border-white/10 bg-white/[0.03] p-1"
                data-testid="ppc-mode-toggle"
              >
                <span
                  className="absolute top-1 bottom-1 left-1 rounded-full transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]"
                  style={{
                    width: "calc(50% - 4px)",
                    transform: mode === "buyer" ? "translateX(0)" : "translateX(100%)",
                    background: mode === "buyer"
                      ? "linear-gradient(135deg,#0055FF 0%,#3d7bff 100%)"
                      : "linear-gradient(135deg,#1e293b 0%,#334155 100%)",
                    boxShadow: mode === "buyer"
                      ? "0 8px 24px rgba(0,85,255,0.4)"
                      : "0 8px 24px rgba(15,23,42,0.4)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => switchMode("buyer")}
                  className={`relative z-10 flex-1 h-11 rounded-full text-[13px] font-semibold transition-colors inline-flex items-center justify-center gap-1.5 ${mode === "buyer" ? "text-white" : "text-white/50 hover:text-white/80"}`}
                  data-testid="ppc-toggle-buyer"
                >
                  <PhoneIncoming className="size-3.5" /> I want to buy calls
                </button>
                <button
                  type="button"
                  onClick={() => switchMode("publisher")}
                  className={`relative z-10 flex-1 h-11 rounded-full text-[13px] font-semibold transition-colors inline-flex items-center justify-center gap-1.5 ${mode === "publisher" ? "text-white" : "text-white/50 hover:text-white/80"}`}
                  data-testid="ppc-toggle-publisher"
                >
                  <PhoneForwarded className="size-3.5" /> I run a call centre
                </button>
              </div>

              {/* Form body */}
              {sent ? (
                <div className="text-center py-10" data-testid="ppc-form-success">
                  <span className="mx-auto grid place-items-center size-14 rounded-full bg-emerald-400/15 text-emerald-300 ring-1 ring-emerald-400/30">
                    <CheckCircle2 className="size-7" />
                  </span>
                  <h3 className="mt-5 text-2xl font-semibold text-white tracking-[-0.02em]">Enquiry received.</h3>
                  <p className="mt-2 text-[14px] text-white/60 leading-relaxed max-w-md mx-auto">
                    Thanks {form.name?.split(" ")[0]}. We&apos;ll come back within one business day with next steps and a
                    sample of the campaign flow you&apos;d be running on.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-5" data-testid="ppc-form-fields">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <DarkField id="ppc-name"    label="Full name *"      testId="ppc-field-name"    value={form.name}    onChange={setField("name")}    placeholder="Marcus Ali" />
                    <DarkField id="ppc-email"   label="Work email *"     testId="ppc-field-email"   value={form.email}   onChange={setField("email")}   placeholder="marcus@company.co.uk" type="email" />
                    <DarkField id="ppc-company" label="Company"          testId="ppc-field-company" value={form.company} onChange={setField("company")} placeholder="Company Ltd" />
                    <DarkField id="ppc-phone"   label="Phone"            testId="ppc-field-phone"   value={form.phone}   onChange={setField("phone")}   placeholder="+44 …" />
                  </div>

                  <div>
                    <Label className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">Primary country</Label>
                    <Select
                      value={form.country}
                      onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}
                    >
                      <SelectTrigger
                        className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white hover:bg-white/[0.06] hover:border-white/20 focus:ring-[#0055FF]/40"
                        data-testid="ppc-field-country"
                      >
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((c) => (
                          <SelectItem key={c.name} value={c.name}>
                            <span className="mr-2" aria-hidden>{c.flag}</span>{c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {mode === "buyer" && (
                    <div className="space-y-4 pt-2 border-t border-white/[0.08]">
                      <SectorConstellation
                        label="Sectors you want to buy"
                        listKey="buyer_sectors"
                        selected={form.buyer_sectors}
                        toggle={toggleSector}
                        tone="brand"
                        gridTestId="ppc-buyer-sectors"
                        chipTestPrefix="ppc-buyer-sector"
                      />
                      <div className="grid sm:grid-cols-2 gap-4">
                        <DarkField id="ppc-monthly-calls" label="Monthly call target" testId="ppc-field-monthly-calls" value={form.buyer_monthly_calls}   onChange={setField("buyer_monthly_calls")}   placeholder="e.g. 500–1,000 calls" />
                        <DarkField id="ppc-target-cpc"    label="Target CPC"          testId="ppc-field-target-cpc"    value={form.buyer_target_cpc_gbp}  onChange={setField("buyer_target_cpc_gbp")}  placeholder="i.e. £45" />
                      </div>
                    </div>
                  )}

                  {mode === "publisher" && (
                    <div className="space-y-4 pt-2 border-t border-white/[0.08]">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <DarkField id="ppc-pub-location" label="Call centre location" testId="ppc-field-location" value={form.publisher_location} onChange={setField("publisher_location")} placeholder="Manchester · Manila · US onshore …" />
                        <DarkField id="ppc-pub-seats"    label="Available seats"      testId="ppc-field-seats"    value={form.publisher_seats}    onChange={setField("publisher_seats")}    placeholder="e.g. 25 seats · UK hours" />
                      </div>
                      <SectorConstellation
                        label="Sectors you can handle"
                        listKey="publisher_sectors"
                        selected={form.publisher_sectors}
                        toggle={toggleSector}
                        tone="slate"
                        gridTestId="ppc-pub-sectors"
                        chipTestPrefix="ppc-pub-sector"
                      />
                      <DarkField id="ppc-current-cpc" label="Current CPC you're getting" testId="ppc-field-current-cpc" value={form.publisher_current_cpc_gbp} onChange={setField("publisher_current_cpc_gbp")} placeholder="£30 avg on auto insurance" />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="ppc-message" className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">Anything else?</Label>
                    <Textarea
                      id="ppc-message"
                      data-testid="ppc-field-message"
                      className="mt-1.5 min-h-[110px] resize-none bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40"
                      value={form.message}
                      onChange={setField("message")}
                      placeholder="Volume goals, compliance stack, timeline, questions…"
                    />
                  </div>

                  {/* Submit row with progress ring */}
                  <div className="flex items-center gap-3 pt-2">
                    <ProgressRing pct={completion} />
                    <Button
                      type="submit"
                      disabled={busy}
                      data-testid="ppc-form-submit"
                      className="flex-1 rounded-full h-12 bg-gradient-to-r from-[#0055FF] to-[#3d7bff] hover:from-[#0044CC] hover:to-[#0055FF] font-semibold gap-1.5 shadow-[0_12px_32px_rgba(0,85,255,0.5)] border border-white/10"
                    >
                      {busy ? <Loader2 className="size-4 animate-spin" /> : <><Send className="size-4" /> Send enquiry <ArrowRight className="size-3.5 ml-1" /></>}
                    </Button>
                  </div>
                  <p className="text-[11px] text-white/40 leading-relaxed">
                    We reply within one business day. No newsletters, no spam. By submitting you agree to our{" "}
                    <Link to="/legal/privacy" className="underline text-white/60 hover:text-white">Privacy Policy</Link>.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});
PayPerCallForm.displayName = "PayPerCallForm";

/* ═════════════════════════════════════════════════════════════════
   FORM helpers
   ═════════════════════════════════════════════════════════════════ */

/** Dark themed input with tight uppercase label. */
function DarkField({ id, label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <Label htmlFor={id} className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">{label}</Label>
      <Input
        id={id}
        data-testid={testId}
        type={type}
        className="mt-1.5 h-11 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#0055FF]/60 focus-visible:ring-[#0055FF]/40 focus-visible:ring-offset-0"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
      />
    </div>
  );
}

/** A staggered chip grid — sectors as a "constellation" of pickable pills. */
function SectorConstellation({ label, listKey, selected, toggle, tone, gridTestId, chipTestPrefix }) {
  const activeCls = tone === "brand"
    ? "border-[#3d7bff] bg-[#0055FF] text-white shadow-[0_4px_18px_rgba(0,85,255,0.5)]"
    : "border-white/60 bg-white text-slate-900 shadow-[0_4px_18px_rgba(255,255,255,0.15)]";
  const idleCls = "border-white/10 bg-white/[0.03] text-white/70 hover:border-white/25 hover:text-white";
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-semibold">{label}</Label>
        <span className="text-[11px] font-mono text-white/40 tabular-nums">
          {selected?.length || 0}/{SECTORS.length} selected
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid={gridTestId}>
        {SECTORS.map((s) => {
          const active = selected?.includes(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key, listKey)}
              className={`px-3 py-1.5 rounded-full border text-[12px] font-medium transition-all ${active ? activeCls : idleCls}`}
              data-testid={`${chipTestPrefix}-${s.key}`}
            >
              {s.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Small progress ring — sits next to the submit button and animates as the form fills. */
function ProgressRing({ pct }) {
  const R = 20;
  const C = 2 * Math.PI * R;
  const offset = C - (C * pct) / 100;
  return (
    <div className="relative shrink-0" title={`${pct}% ready`} data-testid="ppc-form-progress">
      <svg width="52" height="52" className="rotate-[-90deg]">
        <circle cx="26" cy="26" r={R} stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
        <circle
          cx="26" cy="26" r={R}
          stroke="url(#pctGrad)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={offset}
          fill="none"
          style={{ transition: "stroke-dashoffset 0.5s ease-out" }}
        />
        <defs>
          <linearGradient id="pctGrad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="#0055FF" />
            <stop offset="1" stopColor="#34d399" />
          </linearGradient>
        </defs>
      </svg>
      <span className="absolute inset-0 grid place-items-center text-[10.5px] font-semibold text-white tabular-nums">
        {pct}%
      </span>
    </div>
  );
}

/** Ambient orbital lines behind the form section. */
function FormOrbitBackdrop() {
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.15]" preserveAspectRatio="none" viewBox="0 0 1440 900">
      <defs>
        <radialGradient id="orbGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#3d7bff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#3d7bff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="360" cy="480" rx="480" ry="180" stroke="rgba(125,169,255,0.35)" strokeWidth="1" fill="none" strokeDasharray="4 6" />
      <ellipse cx="360" cy="480" rx="620" ry="260" stroke="rgba(125,169,255,0.22)" strokeWidth="1" fill="none" strokeDasharray="2 8" />
      <ellipse cx="360" cy="480" rx="780" ry="360" stroke="rgba(125,169,255,0.12)" strokeWidth="1" fill="none" strokeDasharray="2 12" />
      <circle cx="360" cy="480" r="6" fill="url(#orbGlow)" />
    </svg>
  );
}

/** Left column live enquiry mirror + rotating call ticker + trust strip. */
function EnquiryPreview({ mode, form, completion }) {
  const CALLS = [
    { s: "Auto Insurance",    g: "London",     d: "94s",  ok: true },
    { s: "Mortgages",         g: "New York",   d: "112s", ok: true },
    { s: "Solar",             g: "Manchester", d: "72s",  ok: true },
    { s: "Legal · PI",        g: "Dallas",     d: "68s",  ok: true },
    { s: "Medicare",          g: "Phoenix",    d: "121s", ok: true },
    { s: "HVAC / Plumbing",   g: "Leeds",      d: "58s",  ok: true },
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % CALLS.length), 2200);
    return () => clearInterval(id);
  }, []);
  const visible = [CALLS[i % CALLS.length], CALLS[(i + 1) % CALLS.length], CALLS[(i + 2) % CALLS.length]];

  const sectorsChosen = mode === "buyer" ? form.buyer_sectors : form.publisher_sectors;
  const firstSector = sectorsChosen?.[0]
    ? SECTORS.find((x) => x.key === sectorsChosen[0])?.name
    : null;

  return (
    <div className="space-y-5" data-testid="ppc-form-preview">
      {/* Mirror card — updates live as user types */}
      <div className="relative rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-xl overflow-hidden">
        <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-[#0055FF]/20 blur-3xl pointer-events-none" />
        <div className="relative p-5">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-white/50 font-semibold">
              <Activity className="size-3 text-[#7da9ff]" />
              Your enquiry
            </div>
            <span className="text-[10.5px] font-mono text-emerald-300 tabular-nums">DRAFT · {completion}%</span>
          </div>

          <div className="mt-4 space-y-3">
            <PreviewRow k="Mode"    v={mode === "buyer" ? "Buyer · Advertiser" : "Publisher · Call centre"} accent />
            <PreviewRow k="Contact" v={form.name?.trim() || <span className="text-white/25">—</span>} />
            <PreviewRow k="Email"   v={form.email?.trim() || <span className="text-white/25">—</span>} mono />
            <PreviewRow k="Company" v={form.company?.trim() || <span className="text-white/25">—</span>} />
            <PreviewRow k="Country" v={form.country || <span className="text-white/25">—</span>} />
            <PreviewRow
              k="Sectors"
              v={sectorsChosen?.length ? (
                <span className="tabular-nums">
                  <span className="text-white/90">{sectorsChosen.length}</span>
                  {firstSector ? <span className="text-white/50"> · {firstSector}{sectorsChosen.length > 1 ? ` +${sectorsChosen.length - 1}` : ""}</span> : null}
                </span>
              ) : <span className="text-white/25">—</span>}
            />
            {mode === "buyer" ? (
              <>
                <PreviewRow k="Monthly target" v={form.buyer_monthly_calls?.trim() || <span className="text-white/25">—</span>} mono />
                <PreviewRow k="Target CPC"     v={form.buyer_target_cpc_gbp?.trim() || <span className="text-white/25">—</span>} mono />
              </>
            ) : (
              <>
                <PreviewRow k="Location" v={form.publisher_location?.trim() || <span className="text-white/25">—</span>} />
                <PreviewRow k="Seats"    v={form.publisher_seats?.trim() || <span className="text-white/25">—</span>} mono />
                <PreviewRow k="Current CPC" v={form.publisher_current_cpc_gbp?.trim() || <span className="text-white/25">—</span>} mono />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Rotating live-call ticker */}
      <div className="relative rounded-2xl border border-white/[0.08] bg-[#0a1124]/60 backdrop-blur-md overflow-hidden">
        <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" />
        <div className="relative p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase text-white/50 font-semibold">
              <Waves className="size-3 text-emerald-300" />
              Live · routed while you type
            </div>
            <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
          </div>
          <div className="space-y-1.5">
            {visible.map((e, idx) => (
              <div key={`${i}-${idx}`} className="flex items-center gap-3 rounded-lg bg-white/[0.03] border border-white/[0.06] px-3 py-2 animate-[tickerIn_0.5s_ease-out]">
                <span className="text-[12.5px] text-white/90 flex-1 truncate">{e.s} · {e.g}</span>
                <span className="text-[10.5px] font-mono text-white/60 tabular-nums">{e.d}</span>
                <span className="inline-flex items-center gap-1 text-[10px] text-emerald-300">
                  <span className="size-1.5 rounded-full bg-emerald-400" /> billable
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Trust strip */}
      <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-md p-5">
        <div className="grid grid-cols-3 gap-4 text-center">
          <TrustStat v="1 biz day" l="reply time" />
          <TrustStat v="24" l="live sectors" />
          <TrustStat v="< 300ms" l="routing p95" />
        </div>
        <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-center gap-3 text-[10.5px] font-mono uppercase tracking-widest text-white/40">
          <span>TCPA</span><span className="text-white/20">·</span>
          <span>PECR</span><span className="text-white/20">·</span>
          <span>STIR/SHAKEN</span>
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ k, v, mono = false, accent = false }) {
  return (
    <div className="flex items-baseline gap-3 text-[12.5px]">
      <span className="text-[10.5px] uppercase tracking-[0.16em] text-white/40 w-28 shrink-0">{k}</span>
      <span className={`flex-1 truncate ${mono ? "font-mono tabular-nums" : ""} ${accent ? "text-[#7da9ff] font-semibold" : "text-white/90"}`}>
        {v}
      </span>
    </div>
  );
}
function TrustStat({ v, l }) {
  return (
    <div>
      <div className="text-[18px] font-semibold text-white tabular-nums tracking-[-0.02em]">{v}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-0.5">{l}</div>
    </div>
  );
}

/* ─────────────────  small helpers  ───────────────── */
function Stat({ v, l }) {
  return (
    <div>
      <div className="text-[22px] font-semibold tracking-[-0.025em] text-slate-900 tabular-nums">{v}</div>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">{l}</div>
    </div>
  );
}
function LegendChip({ color, label }) {
  return (
    <div className="inline-flex items-center justify-center gap-1.5">
      <span className={`size-2 rounded-full ${color}`} />
      <span>{label}</span>
    </div>
  );
}
